/** ACT004 用户信息表单热点：绑定 yx_user 字段（与控制台一致） */
export const ACT004_USER_FORM_FIELD_OPTIONS = [
  { value: 'phone', label: '手机号' },
  { value: 'real_name', label: '姓名' },
  { value: 'card_id', label: '身份证' },
  { value: 'company_name', label: '公司名称' }
]

export function act004UserFormFieldLabel(field) {
  const hit = ACT004_USER_FORM_FIELD_OPTIONS.find((o) => o.value === field)
  return hit ? hit.label : field || ''
}

/** 草稿键：优先 index+bind；bind 为空时退化为仅 index（兼容旧数据） */
export function act004UserFormDraftKey(hotspot) {
  const idx = hotspot && hotspot.index != null && hotspot.index !== '' ? String(hotspot.index) : ''
  const bind = hotspot && hotspot.userFieldBind != null ? String(hotspot.userFieldBind).trim() : ''
  if (bind) return `${idx}:${bind}`
  return idx ? `idx:${idx}` : ''
}

/** 按绑定字段读取草稿（合并 index:bind 与 idx:index 两种历史键） */
export function readUserFormDraftByHotspot(draft, hotspot) {
  const d = draft && typeof draft === 'object' ? draft : {}
  const primary = act004UserFormDraftKey(hotspot)
  if (primary && d[primary] != null && String(d[primary]) !== '') {
    return String(d[primary])
  }
  const idx = hotspot && hotspot.index != null ? String(hotspot.index) : ''
  const legacy = idx ? `${idx}:` : ''
  if (legacy && d[legacy] != null) return String(d[legacy])
  const legacyIdx = idx ? `idx:${idx}` : ''
  if (legacyIdx && d[legacyIdx] != null) return String(d[legacyIdx])
  return ''
}

export function act004InputTypeForField(field) {
  const f = String(field || '').trim()
  if (f === 'phone') return 'tel'
  if (f === 'card_id') return 'text'
  return 'text'
}

export const ACT004_CONDITIONAL_JUMP_CONDITION_TYPES = [
  { value: 'PROFILE_FIELDS', label: '完善字段' },
  { value: 'CHECKIN_POINTS', label: '打卡点位' }
]

const PROFILE_FIELD_BINDS = ['phone', 'real_name', 'card_id', 'company_name']

export function normalizeConditionalTargetConditionType(type) {
  const t = String(type || '').trim().toUpperCase()
  return t === 'CHECKIN_POINTS' ? 'CHECKIN_POINTS' : 'PROFILE_FIELDS'
}

export function normalizeConditionalTargetCheckinIndexes(indexes) {
  const arr = Array.isArray(indexes) ? indexes : []
  const out = []
  arr.forEach((raw) => {
    const n = parseInt(raw, 10)
    if (!Number.isFinite(n) || n <= 0 || out.includes(n)) return
    out.push(n)
  })
  return out
}

/** 规范化跳转条件：须已完善的用户资料字段列表 */
export function normalizeConditionalTargetFields(fields) {
  const allowed = {}
  PROFILE_FIELD_BINDS.forEach((f) => {
    allowed[f] = true
  })
  const arr = Array.isArray(fields) ? fields : []
  const out = []
  arr.forEach((raw) => {
    const f = String(raw || '').trim().toLowerCase()
    if (!allowed[f] || out.includes(f)) return
    out.push(f)
  })
  return out
}

/**
 * 所选资料字段均已填写（非空）时返回 true。
 * 条件跳转须传入服务端已落库资料（勿用未提交表单草稿）。
 */
export function isAct004ProfileFieldsComplete(getFieldValue, fields) {
  const required = normalizeConditionalTargetFields(fields)
  if (!required.length) return false
  const getVal = typeof getFieldValue === 'function' ? getFieldValue : () => ''
  return required.every((f) => String(getVal(f) || '').trim() !== '')
}

/** 是否满足条件目标页规则（按条件类型分支） */
export function evaluateConditionalJumpTarget(options) {
  const opts = options && typeof options === 'object' ? options : {}
  const type = normalizeConditionalTargetConditionType(opts.conditionType)
  if (type === 'CHECKIN_POINTS') {
    const indexes = normalizeConditionalTargetCheckinIndexes(opts.checkinIndexes)
    if (!indexes.length || typeof opts.isCheckinPointDone !== 'function') {
      return false
    }
    return indexes.every((idx) => !!opts.isCheckinPointDone(idx))
  }
  return isAct004ProfileFieldsComplete(opts.getProfileFieldValue, opts.profileFields)
}

export function normalizeConditionalTargetRule(rule) {
  if (!rule || typeof rule !== 'object') return null
  const targetPageIndex = parseInt(rule.targetPageIndex, 10)
  if (!Number.isFinite(targetPageIndex) || targetPageIndex < 0) return null
  const conditionType = normalizeConditionalTargetConditionType(rule.conditionType)
  const profileFields = normalizeConditionalTargetFields(rule.profileFields || rule.fields)
  const checkinIndexes = normalizeConditionalTargetCheckinIndexes(rule.checkinIndexes)
  if (conditionType === 'PROFILE_FIELDS' && !profileFields.length) return null
  if (conditionType === 'CHECKIN_POINTS' && !checkinIndexes.length) return null
  return {
    conditionType,
    profileFields,
    checkinIndexes,
    targetPageIndex
  }
}

export function normalizeConditionalTargetRules(source) {
  if (Array.isArray(source)) {
    return source.map((r) => normalizeConditionalTargetRule(r)).filter(Boolean)
  }
  if (!source || typeof source !== 'object') return []
  if (Array.isArray(source.conditionalTargetRules)) {
    return normalizeConditionalTargetRules(source.conditionalTargetRules)
  }
  const legacyPage = parseInt(source.conditionalTargetPageIndex, 10)
  if (!Number.isFinite(legacyPage) || legacyPage < 0) return []
  const legacy = normalizeConditionalTargetRule({
    conditionType: source.conditionalTargetConditionType,
    profileFields: source.conditionalTargetFields,
    checkinIndexes: source.conditionalTargetCheckinIndexes,
    targetPageIndex: legacyPage
  })
  return legacy ? [legacy] : []
}

/** 是否含「完善字段」类条件跳转（触发前需拉取 yx_user 资料校验） */
export function hotspotNeedsProfileFieldsConditionalVerify(rulesSource) {
  const rules = normalizeConditionalTargetRules(rulesSource)
  return rules.some(
    (r) =>
      r.conditionType === 'PROFILE_FIELDS' &&
      Array.isArray(r.profileFields) &&
      r.profileFields.length > 0
  )
}

export function resolveConditionalTargetPageIndex(rulesSource, evaluateOptions, defaultPageIndex) {
  const fallback = Math.max(0, parseInt(defaultPageIndex, 10) || 0)
  const rules = normalizeConditionalTargetRules(rulesSource)
  const opts = evaluateOptions && typeof evaluateOptions === 'object' ? evaluateOptions : {}
  for (let i = 0; i < rules.length; i++) {
    const r = rules[i]
    if (
      evaluateConditionalJumpTarget({
        conditionType: r.conditionType,
        profileFields: r.profileFields,
        checkinIndexes: r.checkinIndexes,
        getProfileFieldValue: opts.getProfileFieldValue,
        isCheckinPointDone: opts.isCheckinPointDone
      })
    ) {
      return r.targetPageIndex
    }
  }
  return fallback
}
